import { useState, useEffect, useCallback, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchRemitBatches, deleteRemitBatch, openRemitPdf, syncClaimMdEras,
  fetchAllSelfPayPayments, fetchAllPaymentPlans,
} from '../services/api';
import BatchRemitModal from '../components/BatchRemitModal';
import SelfPayModal from '../components/SelfPayModal';
import SelfPayEditModal from '../components/SelfPayEditModal';
import RecordInstallmentModal from '../components/RecordInstallmentModal';
import OverrideBanner from '../components/OverrideBanner';
import BulkStatementsPanel from '../components/BulkStatementsPanel';
import UnappliedCashPanel from '../components/UnappliedCashPanel';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import './Payments.css';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_LABELS  = { on_hold: 'On Hold', finalized: 'Finalized' };
const PLAN_STATUS_LABELS = { active: 'Active', completed: 'Completed', defaulted: 'Defaulted', cancelled: 'Cancelled' };
const METHOD_LABELS  = {
  // ⟳ Step 12 (decision A) — the STORED value is `card`; the label a biller
  // reads is unchanged, so migrated history renders exactly as it did. `ach` and
  // `other` are the two members sync payments added.
  cash: 'Cash', check: 'Check', card: 'Credit Card', money_order: 'Money Order',
  online: 'Online', ach: 'ACH', other: 'Other',
  // Not a payment anyone made — the two legs of an internal credit transfer, which
  // net to zero. Labelled so a biller reading the ledger can tell them apart from
  // money that actually came through the door.
  credit_transfer: 'Credit Transfer',
  // ⟳ Step 14 (S14-e) — money that LEFT: a patient credit refunded, posted negative.
  refund: 'Refund',
};
const FREQ_LABELS    = { weekly: 'Weekly', bi_weekly: 'Bi-Weekly', monthly: 'Monthly' };

const PLAN_STATUS_COLORS = {
  active:    { bg: '#eff6ff', color: '#1d4ed8' },
  completed: { bg: '#f0fdf4', color: '#15803d' },
  defaulted: { bg: '#fef2f2', color: '#dc2626' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
};

export default function PaymentsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Section ──────────────────────────────────────────────────────────────
  const canMove = useCanMoveMoney();
  const [section, setSection] = useState('remits'); // 'remits' | 'selfpay' | 'plans' | 'statements' | 'unapplied'

  // ── Insurance Remits ─────────────────────────────────────────────────────
  const [batches, setBatches]   = useState([]);
  const [remitLoading, setRemitLoading] = useState(true);
  const [remitError, setRemitError]     = useState(null);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [syncingEras, setSyncingEras] = useState(false);

  // ── Self-Pay Payments ────────────────────────────────────────────────────
  const [selfPayModal, setSelfPayModal]     = useState(false);
  const [selfPayments, setSelfPayments]     = useState([]);
  const [spLoading, setSpLoading]           = useState(false);
  const [spError, setSpError]               = useState(null);
  const [spSearch, setSpSearch]             = useState('');
  const [spEditModal, setSpEditModal]       = useState(null);  // { mode, payment }
  const [spNotice, setSpNotice]             = useState(null);   // server confirmation after an edit/void
  const [spExpanded, setSpExpanded]         = useState(null);   // payment id whose override trail is open

  // ── Installment Plans ────────────────────────────────────────────────────
  const [plans, setPlans]             = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError]     = useState(null);
  const [plansFilter, setPlansFilter]   = useState('active');
  const [plansSearch, setPlansSearch]   = useState('');
  const [installmentModal, setInstallmentModal] = useState(null); // planId

  // ── Data loaders ──────────────────────────────────────────────────────────
  const loadRemits = useCallback(async () => {
    try {
      setRemitLoading(true); setRemitError(null);
      const res = await fetchRemitBatches();
      setBatches(res.data || []);
    } catch { setRemitError('Failed to load remit batches.'); }
    finally  { setRemitLoading(false); }
  }, []);

  const loadSelfPay = useCallback(async () => {
    try {
      setSpLoading(true); setSpError(null);
      const res = await fetchAllSelfPayPayments();
      setSelfPayments(res.data || []);
    } catch { setSpError('Failed to load self-pay payments.'); }
    finally  { setSpLoading(false); }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      setPlansLoading(true); setPlansError(null);
      const res = await fetchAllPaymentPlans(plansFilter !== 'all' ? { status: plansFilter } : {});
      setPlans(res.data || []);
    } catch { setPlansError('Failed to load payment plans.'); }
    finally  { setPlansLoading(false); }
  }, [plansFilter]);

  useEffect(() => { loadRemits(); }, [loadRemits]);

  useEffect(() => {
    if (section === 'selfpay' && selfPayments.length === 0 && !spLoading) loadSelfPay();
  }, [section]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (section === 'plans') loadPlans();
  }, [section, loadPlans]);

  // ── Deep-link handling ───────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('open') && !params.get('section')) return;

    const sectionParam = params.get('section');
    if (sectionParam === 'selfpay') { setSection('selfpay'); navigate('/revenue/payments', { replace: true }); return; }
    if (sectionParam === 'plans')   { setSection('plans');   navigate('/revenue/payments', { replace: true }); return; }

    if (!params.get('open')) return;

    const batchId  = params.get('batchId');
    const readonly = params.get('readonly') === '1';

    if (batchId) {
      setModal({ batchId: parseInt(batchId, 10), readonly });
    } else {
      const payerOrder = params.get('payerOrder') ? parseInt(params.get('payerOrder'), 10) : null;
      const billedAmt  = params.get('billedAmt')  ? parseFloat(params.get('billedAmt'))    : null;
      const prevPayerName = params.get('prevPayerName');
      const prevRemit = prevPayerName ? {
        payer_name:        prevPayerName,
        billed_amount:     params.get('prevBilledAmt')      ? parseFloat(params.get('prevBilledAmt'))      : null,
        allowed_amount:    params.get('prevAllowedAmt')     ? parseFloat(params.get('prevAllowedAmt'))     : null,
        paid_amount:       params.get('prevPaidAmt')        ? parseFloat(params.get('prevPaidAmt'))        : null,
        contractual_adj:   params.get('prevContractualAdj') ? parseFloat(params.get('prevContractualAdj')) : null,
        other_adj:         params.get('prevOtherAdj')       ? parseFloat(params.get('prevOtherAdj'))       : null,
        patient_resp:      params.get('prevPatientResp')    ? parseFloat(params.get('prevPatientResp'))    : null,
        forwarded_balance: billedAmt,
      } : null;
      setModal({
        batchId: null, readonly: false,
        initialData: {
          mode:         'single',
          claimId:      params.get('claimId')  ? parseInt(params.get('claimId'), 10)  : null,
          payerOrder,
          payerId:      params.get('payerId')  ? parseInt(params.get('payerId'), 10)  : null,
          payerName:    params.get('payerName') || '',
          billedAmount: billedAmt,
          submittedAt:  params.get('submittedAt') || null,
          remitId:      params.get('remitId')  ? parseInt(params.get('remitId'), 10)  : null,
          prevRemit,
        },
      });
    }
    navigate('/revenue/payments', { replace: true });
  }, [location.search, navigate]);

  // ── Remit delete ──────────────────────────────────────────────────────────
  const handleDeleteRemit = async (batchId) => {
    if (!window.confirm('Delete this on-hold remit? The claim remit data will be preserved but unlinked from this batch.')) return;
    setDeleting(batchId);
    try { await deleteRemitBatch(batchId); await loadRemits(); }
    catch { alert('Failed to delete remit batch.'); }
    finally { setDeleting(null); }
  };

  // ── On-demand ERA pull from Claim.MD ───────────────────────────────────────
  // Lets a biller fetch a just-arrived ERA without waiting for the scheduled poll.
  const handleSyncRemits = async () => {
    setSyncingEras(true);
    try {
      const res = await syncClaimMdEras();
      const ingested = res?.ingested ?? 0;
      const matched  = res?.claimsMatched ?? 0;
      await loadRemits();
      window.alert(
        ingested > 0
          ? `Synced ${ingested} new remittance batch(es) from Claim.MD (${matched} claim line(s) matched).`
          : 'No new remittances available from Claim.MD right now.'
      );
    } catch (err) {
      window.alert('Could not sync remittances: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSyncingEras(false);
    }
  };

  // ── Remit PDF (Claim.MD ERA only) ──────────────────────────────────────────
  const handleViewPdf = async (batchId) => {
    setPdfLoading(batchId);
    try { await openRemitPdf(batchId); }
    catch { alert('Could not load the remit PDF for this batch.'); }
    finally { setPdfLoading(null); }
  };

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredBatches = batches.filter((b) => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return b.check_number?.toLowerCase().includes(q) ||
        b.payer_name?.toLowerCase().includes(q) || String(b.check_amount)?.includes(q);
    }
    return true;
  });

  const filteredSelfPay = selfPayments.filter((p) => {
    if (!spSearch.trim()) return true;
    const q = spSearch.trim().toLowerCase();
    return p.patient_name?.toLowerCase().includes(q) ||
      p.claim_number?.toLowerCase().includes(q) ||
      p.reference_number?.toLowerCase().includes(q);
  });

  const filteredPlans = plans.filter((p) => {
    if (!plansSearch.trim()) return true;
    const q = plansSearch.trim().toLowerCase();
    return p.patient_name?.toLowerCase().includes(q) ||
      p.plan_number?.toLowerCase().includes(q);
  });

  const remitCounts = {
    all:       batches.length,
    on_hold:   batches.filter((b) => b.status === 'on_hold').length,
    finalized: batches.filter((b) => b.status === 'finalized').length,
  };

  const planDueSoon = plans.filter((p) => {
    if (!p.next_due_date || p.status !== 'active') return false;
    const due  = new Date(p.next_due_date + 'T12:00:00');
    const now  = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  }).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="payments-page">

      {/* Page header */}
      <div className="payments-header">
        <div>
          <h1 className="payments-title">Payments</h1>
          <p className="payments-subtitle">
            {section === 'remits'  && 'Remittance postings across all payers'}
            {section === 'selfpay' && 'Patient self-pay payment history'}
            {section === 'plans'   && 'Installment payment plans'}
            {section === 'statements' && 'Bulk-print statements for patients with a balance due'}
            {section === 'unapplied' && 'Patient payments from the source system not yet on a claim'}
          </p>
        </div>
        <div className="payments-header-btns">
          <button
            className="payments-btn-selfpay"
            onClick={() => setSelfPayModal(true)}
            disabled={!canMove}
            title={canMove ? undefined : MONEY_ROLE_HINT}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Record Self-Pay
          </button>
          {section === 'remits' && (
            <button
              className="payments-btn-selfpay"
              onClick={handleSyncRemits}
              disabled={syncingEras || !canMove}
              title={canMove ? 'Pull newly received ERAs from Claim.MD now (also runs automatically on a schedule)' : MONEY_ROLE_HINT}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {syncingEras ? 'Syncing…' : 'Sync Remits'}
            </button>
          )}
          <button
            className="payments-btn-record"
            onClick={() => setModal({ batchId: null, readonly: false, initialData: null })}
            disabled={!canMove}
            title={canMove ? undefined : MONEY_ROLE_HINT}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Record Remit
          </button>
        </div>
      </div>

      {/* Top-level section tabs */}
      <div className="payments-section-tabs">
        {[
          { key: 'remits',  label: 'Insurance Remits',   count: batches.length },
          { key: 'selfpay', label: 'Self-Pay Payments',  count: selfPayments.length > 0 ? selfPayments.length : null },
          { key: 'plans',   label: 'Installment Plans',  count: plans.length > 0 ? plans.length : null, alert: planDueSoon > 0 ? planDueSoon : null },
          { key: 'statements', label: 'Patient Statements', count: null },
          { key: 'unapplied', label: 'Unapplied Cash', count: null },
        ].map((s) => (
          <button
            key={s.key}
            className={`payments-section-tab ${section === s.key ? 'active' : ''}`}
            onClick={() => setSection(s.key)}
          >
            {s.label}
            {s.count != null && <span className="payments-section-count">{s.count}</span>}
            {s.alert  != null && <span className="payments-section-alert">{s.alert} due soon</span>}
          </button>
        ))}
      </div>

      {/* ── Insurance Remits ─────────────────────────────────────────────── */}
      {section === 'remits' && (
        <>
          <div className="payments-controls">
            <div className="payments-tabs">
              {['all', 'on_hold', 'finalized'].map((tab) => (
                <button
                  key={tab}
                  className={`payments-tab ${filter === tab ? 'active' : ''}`}
                  onClick={() => setFilter(tab)}
                >
                  {tab === 'all' ? 'All' : STATUS_LABELS[tab]}
                  <span className="payments-tab-count">{remitCounts[tab]}</span>
                </button>
              ))}
            </div>
            <div className="payments-search-wrap">
              <svg className="payments-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className="payments-search"
                type="text"
                placeholder="Search check #, payer, amount…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {remitLoading ? (
            <div className="payments-loading"><div className="payments-spinner" /><span>Loading remit batches…</span></div>
          ) : remitError ? (
            <div className="payments-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {remitError}
              <button className="payments-error-retry" onClick={loadRemits}>Retry</button>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="payments-empty">
              <div className="payments-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </div>
              <p className="payments-empty-title">
                {search || filter !== 'all' ? 'No remits match your filters' : 'No remits posted yet'}
              </p>
              {!search && filter === 'all' && (
                <p className="payments-empty-sub">Click <strong>Record Remit</strong> to post your first remittance batch.</p>
              )}
            </div>
          ) : (
            <div className="payments-table-wrap">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Check #</th><th>Payer</th><th className="num">Check Amount</th><th className="num">Total Paid</th>
                    <th># Claims</th><th>Remit Date</th><th>Date Posted</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.map((batch) => (
                    <tr key={batch.remit_batch_id}>
                      <td className="payments-td-check">{batch.check_number}</td>
                      <td className="payments-td-payer">{batch.payer_name}</td>
                      <td className="payments-td-amount">{fmtCurrency(batch.check_amount)}</td>
                      <td className={`payments-td-amount ${Math.abs(batch.total_paid - batch.check_amount) > 0.01 && batch.status === 'on_hold' ? 'payments-td-mismatch' : ''}`}>
                        {fmtCurrency(batch.total_paid)}
                        {Math.abs(batch.total_paid - batch.check_amount) > 0.01 && batch.status === 'on_hold' && (
                          <span className="payments-mismatch-badge" title="Total paid does not match check amount">!</span>
                        )}
                      </td>
                      <td className="payments-td-center">{batch.claim_count}</td>
                      <td>{fmtDate(batch.remit_date)}</td>
                      <td>{fmtDate(batch.date_posted)}</td>
                      <td>
                        <span className={`payments-status-badge payments-status-${batch.status}`}>{STATUS_LABELS[batch.status]}</span>
                        {batch.override_count > 0 && (
                          <span className="payments-override-chip" title="This remit was corrected after finalizing">Overridden</span>
                        )}
                      </td>
                      <td className="payments-td-actions">
                        {batch.status === 'on_hold' ? (
                          <>
                            <button className="payments-action-btn payments-action-resume" onClick={() => setModal({ batchId: batch.remit_batch_id, readonly: false })} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Resume</button>
                            <button className="payments-action-btn payments-action-delete" onClick={() => handleDeleteRemit(batch.remit_batch_id)} disabled={deleting === batch.remit_batch_id || !canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>
                              {deleting === batch.remit_batch_id ? '…' : 'Delete'}
                            </button>
                          </>
                        ) : (
                          <button className="payments-action-btn payments-action-view" onClick={() => setModal({ batchId: batch.remit_batch_id, readonly: true })}>View</button>
                        )}
                        {batch.claimmd_eraid && (
                          <button
                            className="payments-action-btn payments-action-view"
                            onClick={() => handleViewPdf(batch.remit_batch_id)}
                            disabled={pdfLoading === batch.remit_batch_id}
                            title="View the payer's remittance PDF (EOB) from Claim.MD"
                          >
                            {pdfLoading === batch.remit_batch_id ? '…' : 'PDF'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Self-Pay Payments ─────────────────────────────────────────────── */}
      {section === 'selfpay' && (
        <>
          <div className="payments-controls">
            <div className="payments-search-wrap" style={{ maxWidth: 380 }}>
              <svg className="payments-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className="payments-search"
                type="text"
                placeholder="Search patient, claim #…"
                value={spSearch}
                onChange={(e) => setSpSearch(e.target.value)}
              />
            </div>
            <button className="payments-btn-refresh" onClick={loadSelfPay} title="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

          {spNotice && (
            <div className="payments-notice" role="status">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>{spNotice}</span>
              <button className="payments-notice-close" onClick={() => setSpNotice(null)} aria-label="Dismiss">&times;</button>
            </div>
          )}

          {spLoading ? (
            <div className="payments-loading"><div className="payments-spinner" /><span>Loading self-pay payments…</span></div>
          ) : spError ? (
            <div className="payments-error">{spError}<button className="payments-error-retry" onClick={loadSelfPay}>Retry</button></div>
          ) : filteredSelfPay.length === 0 ? (
            <div className="payments-empty">
              <div className="payments-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                </svg>
              </div>
              <p className="payments-empty-title">{spSearch ? 'No payments match your search' : 'No self-pay payments recorded yet'}</p>
              {!spSearch && <p className="payments-empty-sub">Click <strong>Record Self-Pay</strong> to post the first patient payment.</p>}
            </div>
          ) : (
            <div className="payments-table-wrap">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Payment Date</th><th>Patient</th><th>Claim #</th><th>Service Date</th>
                    <th>Method</th><th>Reference #</th><th className="payments-td-amount num">Amount</th>
                    <th>Notes</th><th>Posted By</th><th>Plan</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSelfPay.map((p) => {
                    const isVoided     = !!p.voided_at;
                    const isPlan       = !!p.payment_plan_id;
                    const overrideList = p.overrides || [];
                    const hasOverrides = overrideList.length > 0;
                    const isExpanded   = spExpanded === p.patient_payment_id;
                    return (
                      <Fragment key={p.patient_payment_id}>
                        <tr className={isVoided ? 'payments-sp-voided' : ''}>
                          <td>
                            {fmtDate(p.payment_date)}
                            {isVoided    && <span className="payments-void-chip" title={p.void_reason || ''}>Voided</span>}
                            {hasOverrides && (
                              <button
                                className="payments-edited-chip"
                                onClick={() => setSpExpanded(isExpanded ? null : p.patient_payment_id)}
                                title="Show change history"
                              >
                                Edited{overrideList.length > 1 ? ` ×${overrideList.length}` : ''}
                              </button>
                            )}
                          </td>
                          <td className="payments-td-payer">{p.patient_name}</td>
                          <td className="payments-td-check">{p.claim_number}</td>
                          <td>{fmtDate(p.service_date)}</td>
                          <td>{METHOD_LABELS[p.payment_method] ?? p.payment_method}</td>
                          <td style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '0.85rem' }}>{p.reference_number || '—'}</td>
                          <td className="payments-td-amount" style={{ color: '#111827', fontWeight: 600 }}>{fmtCurrency(p.amount)}</td>
                          <td className="payments-td-notes" title={p.notes || ''}>{p.notes || '—'}</td>
                          <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>{p.posted_by_first} {p.posted_by_last}</td>
                          <td>
                            {isPlan
                              ? <span className="payments-plan-badge">Plan</span>
                              : <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>One-time</span>
                            }
                          </td>
                          <td className="payments-td-actions">
                            {isVoided ? (
                              <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                            ) : isPlan ? (
                              <span style={{ color: '#9ca3af', fontSize: '0.78rem' }} title="Manage from the payment plan">Plan-linked</span>
                            ) : (
                              <>
                                <button className="payments-action-btn payments-action-resume" onClick={() => setSpEditModal({ mode: 'edit', payment: p })} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Edit</button>
                                <button className="payments-action-btn payments-action-delete" onClick={() => setSpEditModal({ mode: 'void', payment: p })} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Void</button>
                              </>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="payments-sp-trail-row">
                            <td colSpan={11}>
                              <OverrideBanner overrides={overrideList} compact />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Installment Plans ─────────────────────────────────────────────── */}
      {section === 'plans' && (
        <>
          <div className="payments-controls">
            <div className="payments-tabs">
              {[
                { key: 'active',    label: 'Active' },
                { key: 'all',       label: 'All' },
                { key: 'completed', label: 'Completed' },
                { key: 'defaulted', label: 'Defaulted' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`payments-tab ${plansFilter === tab.key ? 'active' : ''}`}
                  onClick={() => setPlansFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div className="payments-search-wrap">
                <svg className="payments-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="payments-search"
                  type="text"
                  placeholder="Search patient, plan #…"
                  value={plansSearch}
                  onChange={(e) => setPlansSearch(e.target.value)}
                />
              </div>
              <button className="payments-btn-refresh" onClick={loadPlans} title="Refresh">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
            </div>
          </div>

          {plansLoading ? (
            <div className="payments-loading"><div className="payments-spinner" /><span>Loading payment plans…</span></div>
          ) : plansError ? (
            <div className="payments-error">{plansError}<button className="payments-error-retry" onClick={loadPlans}>Retry</button></div>
          ) : filteredPlans.length === 0 ? (
            <div className="payments-empty">
              <div className="payments-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <p className="payments-empty-title">{plansSearch ? 'No plans match your search' : 'No payment plans found'}</p>
              {!plansSearch && <p className="payments-empty-sub">Payment plans are created from the Patient Summary page.</p>}
            </div>
          ) : (
            <div className="payments-table-wrap">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Plan #</th><th>Patient</th><th className="num">Total</th><th>Paid</th><th className="num">Remaining</th>
                    <th className="num">Installment</th><th>Frequency</th><th>Next Due</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlans.map((plan) => {
                    const pct        = plan.total_amount > 0
                      ? Math.round((parseFloat(plan.amount_paid) / parseFloat(plan.total_amount)) * 100)
                      : 0;
                    const statusStyle = PLAN_STATUS_COLORS[plan.status] ?? PLAN_STATUS_COLORS.active;
                    const isDueSoon   = plan.status === 'active' && plan.next_due_date && (() => {
                      const due = new Date(plan.next_due_date + 'T12:00:00');
                      const diff = (due - new Date()) / (1000 * 60 * 60 * 24);
                      return diff >= 0 && diff <= 7;
                    })();
                    const isOverdue   = plan.status === 'active' && plan.next_due_date && new Date(plan.next_due_date + 'T12:00:00') < new Date();

                    return (
                      <tr key={plan.payment_plan_id}>
                        <td className="payments-td-check">{plan.plan_number}</td>
                        <td className="payments-td-payer">{plan.patient_name}</td>
                        <td className="payments-td-amount">{fmtCurrency(plan.total_amount)}</td>
                        <td>
                          <div className="payments-plan-progress">
                            <div className="payments-plan-bar">
                              <div className="payments-plan-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="payments-plan-pct">{pct}%</span>
                          </div>
                        </td>
                        <td className="payments-td-amount" style={{ color: '#171363', fontWeight: 600 }}>
                          {fmtCurrency(plan.remaining_balance)}
                        </td>
                        <td className="payments-td-amount">{fmtCurrency(plan.installment_amount)}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{FREQ_LABELS[plan.frequency]}</td>
                        <td>
                          {plan.next_due_date ? (
                            <span className={isOverdue ? 'payments-overdue' : isDueSoon ? 'payments-due-soon' : ''}>
                              {fmtDate(plan.next_due_date)}
                            </span>
                          ) : '—'}
                          {plan.missed_installments > 0 && (
                            <span className="payments-missed-badge">{plan.missed_installments} missed</span>
                          )}
                        </td>
                        <td>
                          <span
                            className="payments-status-badge"
                            style={{ background: statusStyle.bg, color: statusStyle.color }}
                          >
                            {PLAN_STATUS_LABELS[plan.status] ?? plan.status}
                          </span>
                        </td>
                        <td className="payments-td-actions">
                          {plan.status === 'active' && (
                            <button
                              className="payments-action-btn payments-action-resume"
                              onClick={() => setInstallmentModal(plan.payment_plan_id)}
                              disabled={!canMove}
                              title={canMove ? undefined : MONEY_ROLE_HINT}
                            >
                              Record Payment
                            </button>
                          )}
                          <button
                            className="payments-action-btn payments-action-view"
                            onClick={() => navigate(`/patients/${plan.patient_id}`)}
                          >
                            Patient
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Patient Statements (bulk print) ──────────────────────────────── */}
      {section === 'statements' && <BulkStatementsPanel />}

      {/* ── Unapplied patient cash (plan Step 12, S12-d) ─────────────────── */}
      {section === 'unapplied' && <UnappliedCashPanel />}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {modal !== null && (
        <BatchRemitModal
          batchId={modal.batchId}
          readonly={modal.readonly}
          initialData={modal.initialData}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadRemits(); }}
        />
      )}

      {selfPayModal && (
        <SelfPayModal
          onClose={() => setSelfPayModal(false)}
          onSaved={() => {
            setSelfPayModal(false);
            setSection('selfpay');
            loadSelfPay();
          }}
        />
      )}

      {installmentModal !== null && (
        <RecordInstallmentModal
          planId={installmentModal}
          onClose={() => setInstallmentModal(null)}
          onSaved={() => { setInstallmentModal(null); loadPlans(); }}
        />
      )}

      {spEditModal && (
        <SelfPayEditModal
          mode={spEditModal.mode}
          payment={spEditModal.payment}
          onClose={() => setSpEditModal(null)}
          onSaved={(message) => { setSpEditModal(null); setSpNotice(message || null); loadSelfPay(); }}
        />
      )}
    </div>
  );
}
